import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { StammdatenService } from '../../stammdaten/stammdaten.service';
import { Client } from '../../client/classes/client';
import { MessageService } from '../../../services/message-service/message.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { KieneApi } from '../../../services/backend-api/kiene-backend-api.service';

@Component({
    selector: 'kiene-betrieb-benutzer-zuweisen-dialog',
    templateUrl: './betrieb-benutzer-zuweisen-dialog.component.html',
    styleUrls: ['./betrieb-benutzer-zuweisen-dialog.component.scss'],
})
export class BetriebBenutzerZuweisenDialogComponent implements OnInit, OnDestroy {
    betriebe: Client[] = [];
    isLoading = false;
    pendingKundeId: number | null = null;
    private assignedIds = new Set<number>();
    private freigabeIds = new Set<number>();
    private loadSub: Subscription;

    constructor(
        private readonly stammdatenService: StammdatenService,
        private readonly messageService: MessageService,
        public dialogRef: MatDialogRef<BetriebBenutzerZuweisenDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public user: any,
    ) {}

    ngOnInit(): void {
        this.load();
    }

    ngOnDestroy(): void {
        this.loadSub?.unsubscribe();
    }

    isAssigned(betrieb: Client): boolean {
        const id = Number(betrieb?.kunde_id);
        return id > 0 && this.assignedIds.has(id);
    }

    isFreigegeben(betrieb: Client): boolean {
        const id = Number(betrieb?.kunde_id);
        return id > 0 && this.freigabeIds.has(id);
    }

    betriebChanged(event: MatCheckboxChange, betrieb: Client) {
        const kundeId = Number(betrieb?.kunde_id);
        const benutzerId = Number(this.user?.benutzer_id);
        const assign = !!event?.checked;

        if (!(kundeId > 0) || !(benutzerId > 0) || this.pendingKundeId != null) {
            event.source.checked = this.isAssigned(betrieb);
            return;
        }

        this.pendingKundeId = kundeId;
        if (assign) {
            this.assignedIds.add(kundeId);
        } else {
            this.assignedIds.delete(kundeId);
        }

        const req = assign
            ? this.stammdatenService.kundeBenutzerZuweisen(benutzerId, kundeId)
            : this.stammdatenService.kundeBenutzerEntfernen(benutzerId, kundeId);

        req.subscribe({
            next: () => {
                this.pendingKundeId = null;
                this.messageService.infoMessage(
                    assign ? 'Betrieb erfolgreich zugewiesen.' : 'Betrieb erfolgreich entfernt.',
                );
            },
            error: err => {
                if (assign) {
                    this.assignedIds.delete(kundeId);
                } else {
                    this.assignedIds.add(kundeId);
                }
                event.source.checked = this.isAssigned(betrieb);
                this.pendingKundeId = null;
                this.messageService.errorMessage(
                    assign ? 'Fehler beim Betrieb zuweisen!' : 'Fehler beim Betrieb entfernen!',
                    err,
                );
            },
        });
    }

    private load() {
        this.isLoading = true;
        this.loadSub?.unsubscribe();
        const benutzerId = Number(this.user?.benutzer_id);
        if (!(benutzerId > 0)) {
            this.isLoading = false;
            return;
        }
        const empty: KieneApi = { records: [], count: 0 };
        this.loadSub = forkJoin({
            meine: this.stammdatenService.ladeMeineVvvos(),
            freigaben: this.stammdatenService.ladeFreigegebeneVvvos().pipe(catchError(() => of(empty))),
            zugewiesen: this.stammdatenService.ladeZugewieseneKunden(benutzerId),
        }).subscribe({
            next: ({ meine, freigaben, zugewiesen }) => {
                const meineList: Client[] = meine?.records ?? [];
                const freigabenList: Client[] = freigaben?.records ?? [];
                const assignedList: Client[] = zugewiesen?.records ?? [];
                this.assignedIds = new Set(
                    assignedList.map(k => Number(k?.kunde_id)).filter(id => id > 0),
                );
                this.freigabeIds = new Set(
                    freigabenList.map(k => Number(k?.kunde_id)).filter(id => id > 0),
                );
                const byId = new Map<number, Client>();
                for (const b of [...meineList, ...freigabenList, ...assignedList]) {
                    const id = Number(b?.kunde_id);
                    if (id > 0 && !byId.has(id)) {
                        byId.set(id, b);
                    }
                }
                this.betriebe = Array.from(byId.values()).sort((a, b) =>
                    String(a?.bezeichnung_final || a?.bezeichnung || '').localeCompare(
                        String(b?.bezeichnung_final || b?.bezeichnung || ''),
                        'de',
                    ),
                );
                this.isLoading = false;
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Laden der Betriebe!', err);
                this.isLoading = false;
            },
        });
    }
}
